import React, { useRef } from 'react'
import {
  Box,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material'
import { Controller } from 'react-hook-form'
import { StateInput } from '../shared/atom/inputs/StateInput'
import { CityInput } from '../shared/atom/inputs/CityInput'
import { DatePicker } from '@mui/x-date-pickers'
import InfoCircle from '@meprism/shared/src/assets/icons/onboarding/info-circle.png'
import { MpButton } from '../shared'
import MpTheme from '@meprism/shared/src/config/MpTheme'
import { useOnboardingStep1Form } from '../../utils/hooks/useOnboardingStep1Form'
import { OnboardingStepper } from './OnboardingStepper'
import Loader from '@meprism/shared/src/components/atoms/Loader'
import MpLoadingIcon from '../shared/atom/MpLoadingIcon'

const autofillSx = {
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px #1A1B20 inset !important',
    transition: 'background-color 5000s ease-in-out 0s',
  },
}

const labelSx = {
  color: '#EFEDFDB2',
  fontFamily: 'InterRegular',
  fontSize: '14px',
  mb: 0.5,
}

export const autocompletePaperSx = {
  backgroundColor: '#222329',
  color: '#FFFFFF',
  '*::-webkit-scrollbar': {
    width: '8px',
  },
  '*::-webkit-scrollbar-track': {
    backgroundColor: '#222329',
  },
  '*::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
  '*::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiAutocomplete-option': {
    fontFamily: 'InterRegular !important',
    fontSize: '14px !important',
    color: '#FFFFFF !important',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
    },
  },
}

interface OnboardingStep1FormProps {
  onNext: () => void
  grant?: boolean
  isUpdate?: boolean
}

export const OnboardingStep1Form = ({ onNext, isUpdate }: OnboardingStep1FormProps) => {
  const {
    firstNameArray,
    middleNameArray,
    lastNameArray,
    addressArray,
    register,
    errors,
    isSubmitting,
    isValid,
    isLoading,
    control,
    handleSubmit,
    submitHandler,
    trigger,
    setValue,
    watch,
  } = useOnboardingStep1Form({
    onSubmitSuccess: onNext,
    isUpdate,
  })

  const addressValues = watch('address')

  const handleStateChange = (index: number, stateCode: string) => {
    setValue(`address.${index}.state`, stateCode as any)
    setValue(`address.${index}.city`, '')
    trigger(`address.${index}.state`)
  }

  const handleCityChange = (index: number, cityName: string) => {
    setValue(`address.${index}.city`, cityName)
    trigger(`address.${index}.city`)
  }

  const dobInputRef = useRef<HTMLInputElement | null>(null)

  if (isLoading) {
    return (
      <Box sx={{ mt: 16, display: 'flex', justifyContent: 'center' }}>
        <MpLoadingIcon positioned />
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ textAlign: 'left', alignItems: 'center', px: 3, mt: 16, mb:10, maxWidth: MpTheme.layouts.widths.sm, mx: 'auto' }}>
        <OnboardingStepper currentStep={1} />
      </Box>

      <Stack
        component="form"
        spacing={2}
        sx={{
          maxWidth: MpTheme.layouts.widths.sm,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 3,
          mt: 5,
          '& .MuiFormLabel-asterisk': { color: 'red' },
          '& .MuiFormHelperText-root': { fontFamily: 'InterRegular', fontSize: '14px', letterSpacing: '0.02rem' },
        }}
      >
        <Stack spacing={2} sx={{ width: '100%' }}>

          {/* First Name + Last Name row */}
          {firstNameArray.fields.map((field, index) => (
            <Box key={field.id} sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx}>First Name *</Typography>
                <TextField
                  fullWidth
                  placeholder="Enter first name here"
                  required
                  error={!!(errors?.firstName?.[index] as any)?.value}
                  helperText={(errors?.firstName?.[index] as any)?.value?.message || ' '}
                  FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                  InputProps={{ sx: autofillSx }}
                  {...register(`firstName.${index}.value` as const, {
                    onBlur: () => trigger(`firstName.${index}.value`),
                  })}
                />
              </Box>
              {lastNameArray.fields[index] && (
                <Box sx={{ flex: 1 }}>
                  <Typography sx={labelSx}>Last Name *</Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter last name here"
                    required
                    error={!!(errors?.lastName?.[index] as any)?.value}
                    helperText={(errors?.lastName?.[index] as any)?.value?.message || ' '}
                    FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                    InputProps={{ sx: autofillSx }}
                    {...register(`lastName.${index}.value` as const, {
                      onBlur: () => trigger(`lastName.${index}.value`),
                    })}
                  />
                </Box>
              )}
            </Box>
          ))}

          {/* Middle Name */}
          {middleNameArray.fields.map((field, index) => (
            <Box key={field.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography sx={labelSx}>Middle Name or Initial *</Typography>
                <Tooltip
                  title="If you have no middle name, enter 'NMN' in the middle name field. If you have a middle name, not providing one will result in insufficient scan results."
                  placement="bottom"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontFamily: 'InterRegular',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#1A202C',
                        backgroundColor: '#A0AEC0',
                        borderRadius: '12px',
                        padding: '10px 2px',
                        ml: 40,
                        maxWidth: '520px',
                        textAlign: 'center',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                      }
                    },
                    arrow: {
                      sx: {
                        color: '#A0AEC0',
                        fontSize: '20px',
                        ml:-20
                      }
                    }
                  }}>
                  <IconButton component="span" sx={{ padding: 0, position: 'relative', top: -2, ml: 1 }}>
                    <img src={InfoCircle} alt="info" style={{ width: '16px', height: '16px', filter: 'brightness(0) saturate(100%) invert(72%) sepia(10%) saturate(400%) hue-rotate(180deg) brightness(95%) contrast(85%)' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                placeholder="Enter middle name or initial here"
                error={!!(errors?.middleName?.[index] as any)?.value}
                helperText={(errors?.middleName?.[index] as any)?.value?.message || ' '}
                FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                InputProps={{ sx: autofillSx }}
                {...register(`middleName.${index}.value` as const, {
                  onBlur: () => trigger(`middleName.${index}.value`),
                })}
              />
            </Box>
          ))}

          {/* Birthdate */}
          <Box sx={{ width: '100%' }}>
            <Typography sx={labelSx}>Date of Birth: MM/DD/YYYY *</Typography>
            <Controller
              name="birthdate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value || null}
                  disableFuture
                  onChange={(newValue) => { field.onChange(newValue); trigger('birthdate') }}
                  OpenPickerButtonProps={{ style: { color: '#6F8EF5' } }}
                  renderInput={(params) => (
                    <TextField
                      fullWidth
                      {...params}
                      inputRef={(el) => {
                        dobInputRef.current = el
                        if (typeof params.inputRef === 'function') params.inputRef(el)
                        else if (params.inputRef) (params.inputRef as any).current = el
                      }}
                      sx={autofillSx}
                      placeholder="Enter birthday here"
                      error={!!errors?.birthdate}
                      helperText={(errors?.birthdate as any)?.message ?? ' '}
                      FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0 } }}
                      onBlur={async () => { await trigger('birthdate'); field.onBlur() }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' || e.key === 'Delete' || e.key.startsWith('Arrow')) return
                        requestAnimationFrame(() => {
                          const el = dobInputRef.current
                          if (!el) return
                          const pos = el.selectionStart ?? 0
                          if (el.value[pos] === '/') {
                            el.setSelectionRange(pos + 1, pos + 1)
                          }
                        })
                      }}
                    />
                  )}
                />
              )}
            />
          </Box>

          {/* City + State row */}
          {addressArray.fields.map((field, index) => (
            <Box key={field.id} sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx}>State *</Typography>
                <Controller
                  name={`address.${index}.state` as const}
                  control={control}
                  render={({ field: stateField }) => (
                    <StateInput
                      value={stateField.value}
                      onChange={(_e, newValue) => handleStateChange(index, newValue)}
                      error={!!errors?.address?.[index]?.state}
                      helperText={errors?.address?.[index]?.state?.message || ' '}
                      placeholder="Enter state here"
                      paperSx={autocompletePaperSx}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx}>City *</Typography>
                <Controller
                  name={`address.${index}.city` as const}
                  control={control}
                  render={({ field: cityField }) => (
                    <CityInput
                      value={cityField.value || ''}
                      stateCode={addressValues?.[index]?.state || addressArray.fields[index]?.state}
                      onChange={(_e, newValue) => handleCityChange(index, newValue)}
                      error={!!errors?.address?.[index]?.city}
                      helperText={errors?.address?.[index]?.city?.message || ' '}
                      placeholder="Enter city here"
                      paperSx={autocompletePaperSx}
                    />
                  )}
                />
              </Box>
            </Box>
          ))}
        </Stack>

        <Box style={{ width: '100%', marginTop: '20px' }}>
        <MpButton
          type="submit"
          variant="contained"
          disabled={isSubmitting || !isValid}
          size="large"
          color="darkPurple"
          sx={{ width: '100%' }}
          onClick={handleSubmit(submitHandler)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', whiteSpace: 'nowrap', width: '25%', '& .MuiCircularProgress-root': { color: '#5856d6 !important', width: '20px !important', height: '20px !important' } }}>
            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>Next</Box>
            <Loader loading={isSubmitting} />
          </Box>
        </MpButton>
        </Box>

      </Stack>
    </>
  )
}
